import { createRouter, createWebHistory } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle';

const router = createRouter({
    history: createWebHistory(),
    scrollBehavior() {
        return { top: 0 };
    },
    routes: [
        // ── Público (con layout institucional) ─────────────────────
        {
            path: '/',
            component: () => import('@/components/layout/PublicLayout.vue'),
            children: [
                {
                    path: '',
                    name: 'home',
                    component: () => import('@/views/public/HomeView.vue'),
                    meta: { public: true, title: 'Inicio' }
                },
                {
                    path: 'nosotros',
                    name: 'nosotros',
                    component: () => import('@/views/public/NosotrosView.vue'),
                    meta: { public: true, title: 'Nosotros' }
                },
                {
                    path: 'ganaderia',
                    name: 'ganaderia-publico',
                    component: () => import('@/views/public/GanaderiaView.vue'),
                    meta: { public: true, title: 'Ganadería' }
                },
                {
                    path: 'agricultura',
                    name: 'agricultura-publico',
                    component: () => import('@/views/public/AgriculturaView.vue'),
                    meta: { public: true, title: 'Agricultura' }
                },
                {
                    path: 'forestal',
                    name: 'forestal-publico',
                    component: () => import('@/views/public/ForestalView.vue'),
                    meta: { public: true, title: 'Forestal' }
                },
                {
                    path: 'establecimientos',
                    name: 'establecimientos-publico',
                    component: () => import('@/views/public/EstablecimientosView.vue'),
                    meta: { public: true, title: 'Establecimientos' }
                },
                {
                    path: 'sustentabilidad',
                    name: 'sustentabilidad',
                    component: () => import('@/views/public/SustentabilidadView.vue'),
                    meta: { public: true, title: 'Sustentabilidad' }
                },
                {
                    path: 'galeria',
                    name: 'galeria',
                    component: () => import('@/views/public/GaleriaView.vue'),
                    meta: { public: true, title: 'Galería' }
                },
                {
                    path: 'contacto',
                    name: 'contacto',
                    component: () => import('@/views/public/ContactoView.vue'),
                    meta: { public: true, title: 'Contacto' }
                }
            ]
        },

        // ── Auth (standalone, sin layout) ──────────────────────────
        {
            path: '/login',
            name: 'login',
            component: () => import('@/views/auth/LoginView.vue'),
            meta: { public: true, title: 'Iniciar sesión' }
        },
        {
            path: '/registro',
            name: 'registro',
            component: () => import('@/views/auth/RegistroView.vue'),
            meta: { public: true, title: 'Registro' }
        },
        {
            path: '/olvide-password',
            name: 'olvide-password',
            component: () => import('@/views/auth/OlvidePasswordView.vue'),
            meta: { public: true, title: 'Recuperar contraseña' }
        },
        {
            path: '/reset-password',
            name: 'reset-password',
            component: () => import('@/views/auth/ResetPasswordView.vue'),
            meta: { public: true, title: 'Restablecer contraseña' }
        },

        // ── Backoffice (con layout admin) ──────────────────────────
        {
            path: '/backoffice',
            component: () => import('@/components/layout/BackofficeLayout.vue'),
            children: [
                {
                    path: '',
                    name: 'dashboard',
                    component: () => import('@/views/backoffice/DashboardView.vue'),
                    meta: { title: 'Dashboard' }
                },
                {
                    path: 'ganaderia',
                    name: 'ganaderia',
                    component: () => import('@/views/backoffice/ganaderia/GanaderiaView.vue'),
                    meta: { title: 'Ganadería' }
                },
                {
                    path: 'hacienda/tropas',
                    name: 'hacienda-tropas',
                    component: () => import('@/views/backoffice/hacienda/TropasListView.vue'),
                    meta: { title: 'Tropas' }
                },
                {
                    path: 'hacienda/tropas/:id',
                    name: 'hacienda-tropa-detalle',
                    component: () => import('@/views/backoffice/hacienda/TropaDetailView.vue'),
                    meta: { title: 'Tropa' }
                },
                {
                    path: 'hacienda/animales/:id',
                    name: 'hacienda-animal-detalle',
                    component: () => import('@/views/backoffice/hacienda/AnimalDetailView.vue'),
                    meta: { title: 'Animal' }
                },
                {
                    path: 'hacienda/movimientos',
                    name: 'hacienda-movimientos',
                    component: () => import('@/views/backoffice/hacienda/MovimientosListView.vue'),
                    meta: { title: 'Movimientos de hacienda' }
                },
                {
                    path: 'hacienda/precios-mercado',
                    name: 'hacienda-precios-mercado',
                    component: () => import('@/views/backoffice/hacienda/PreciosMercadoView.vue'),
                    meta: { title: 'Precios de mercado' }
                },
                {
                    path: 'hacienda/configuracion',
                    name: 'hacienda-configuracion',
                    component: () => import('@/views/backoffice/hacienda/HaciendaConfigView.vue'),
                    meta: { title: 'Configuración de hacienda', area: 'ConfiguracionSistema' }
                },
                {
                    path: 'establecimientos',
                    name: 'establecimientos',
                    component: () => import('@/views/backoffice/establecimientos/EstablecimientosListView.vue'),
                    meta: { title: 'Establecimientos' }
                },
                {
                    path: 'establecimientos/:id',
                    name: 'establecimiento-detalle',
                    component: () => import('@/views/backoffice/establecimientos/EstablecimientoDetailView.vue'),
                    meta: { title: 'Establecimiento' }
                },
                {
                    path: 'lotes',
                    name: 'lotes',
                    component: () => import('@/views/backoffice/lotes/LotesListView.vue'),
                    meta: { title: 'Lotes' }
                },
                {
                    path: 'lotes/eliminados',
                    name: 'lotes-eliminados',
                    component: () => import('@/views/backoffice/lotes/LotesEliminadosView.vue'),
                    meta: { title: 'Lotes eliminados' }
                },
                {
                    path: 'agricultura',
                    name: 'agricultura',
                    component: () => import('@/views/backoffice/agricultura/AgriculturaView.vue'),
                    meta: { title: 'Agricultura' }
                },
                {
                    path: 'administracion',
                    name: 'administracion',
                    component: () => import('@/views/backoffice/administracion/AdministracionView.vue'),
                    meta: { title: 'Administración' }
                },
                {
                    path: 'contabilidad',
                    name: 'contabilidad',
                    component: () => import('@/views/backoffice/contabilidad/ContabilidadView.vue'),
                    meta: { title: 'Contabilidad' }
                },
                {
                    path: 'configuracion/usuarios',
                    name: 'configuracion-usuarios',
                    component: () => import('@/views/backoffice/configuracion/UsuariosView.vue'),
                    meta: { title: 'Usuarios', area: 'ConfiguracionSistema' }
                },
                {
                    path: 'configuracion/smtp',
                    name: 'configuracion-smtp',
                    component: () => import('@/views/backoffice/configuracion/SmtpConfigView.vue'),
                    meta: { title: 'Configuración SMTP', area: 'ConfiguracionSistema' }
                },
                {
                    path: 'cuenta/perfil',
                    name: 'cuenta-perfil',
                    component: () => import('@/views/backoffice/cuenta/PerfilView.vue'),
                    meta: { title: 'Mi Perfil' }
                },
                {
                    path: 'cuenta/password',
                    name: 'cuenta-password',
                    component: () => import('@/views/backoffice/cuenta/PasswordView.vue'),
                    meta: { title: 'Contraseña y Seguridad' }
                },
                {
                    path: 'cuenta/sesiones',
                    name: 'cuenta-sesiones',
                    component: () => import('@/views/backoffice/cuenta/SesionesView.vue'),
                    meta: { title: 'Mis Sesiones' }
                }
            ]
        },

        // ── Clima (standalone, mobile-friendly) ────────────────────
        {
            path: '/clima/cargar',
            name: 'clima-cargar',
            component: () => import('@/views/clima/CargaRapidaView.vue'),
            meta: { title: 'Cargar medición climática' }
        },

        // ── Catch-all ──────────────────────────────────────────────
        {
            path: '/:pathMatch(.*)*',
            redirect: '/'
        }
    ]
});

// ── Guards ──────────────────────────────────────────────────────
// Importar store de forma lazy para evitar problemas de inicialización circular.
// El guard se ejecuta después de que Pinia esté registrado (init en main.js).
let _useUserStore = null;

function getUserStore() {
    if (!_useUserStore) {
        // Dynamic import no es posible en beforeEach sincrono,
        // pero al momento del primer beforeEach, main.js ya registro Pinia y el store existe.
        // Usamos import directo — el módulo ya está cargado en memoria.
        _useUserStore = () => {
            const mod = import.meta.glob('../stores/userStore.js', { eager: true });
            const key = Object.keys(mod)[0];
            return mod[key].useUserStore();
        };
    }
    try {
        return _useUserStore();
    } catch {
        return null;
    }
}

router.beforeEach((to) => {
    const userStore = getUserStore();
    if (!userStore) return true;

    const isPublic = to.meta.public;
    const isAuth = userStore.isAuthenticated;

    // 1. Autenticado yendo a login/registro → redirigir a backoffice
    if (isAuth && (to.name === 'login' || to.name === 'registro')) {
        return '/backoffice';
    }

    // 2. Ruta no pública sin auth → redirigir a login
    if (!isPublic && !isAuth) {
        return { path: '/login', query: { redirect: to.fullPath } };
    }

    // 3. Ruta de backoffice sin rol BO → redirigir a home
    if (to.path.startsWith('/backoffice') && isAuth && !userStore.esBackoffice) {
        return '/';
    }

    // 4. Ruta de configuración sin Administrador → redirigir a dashboard
    if (to.meta.area === 'ConfiguracionSistema' && isAuth && !userStore.esAdministrador) {
        return '/backoffice';
    }

    return true;
});

// ── After each: título de página ────────────────────────────────
router.afterEach((to) => {
    usePageTitle(to.meta.title || '');
});

export default router;
